/**
 * SQ OFF / Add More modal — disclosed quantity rules by exchange.
 * Exchanges in {@link EXCHANGES_FIXED_ZERO_DISCLOSED} always send 0 and ignore header %.
 */

/** @type {ReadonlySet<string>} Uppercase exchange codes */
export const EXCHANGES_FIXED_ZERO_DISCLOSED = new Set(["NFO"]);

export function exchangeForcesZeroDisclosed(exchange) {
  return EXCHANGES_FIXED_ZERO_DISCLOSED.has(String(exchange ?? "").trim().toUpperCase());
}

export function normalizePositiveQtyNumeric(raw) {
  const n = parseFloat(String(raw ?? "").trim());
  if (Number.isNaN(n)) return 0;
  return n < 0 ? Math.abs(n) : n;
}

export function clampDisclosedPct(pct) {
  const n = Number(pct);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

/** Disclosed qty from % of row quantity (header “apply to all”). */
export function disclosedQtyFromPercent(qty, pct) {
  const q = normalizePositiveQtyNumeric(qty);
  const p = clampDisclosedPct(pct);
  const raw = Math.round((q * p) / 100);
  return Math.min(Math.max(0, raw), q);
}

/** Per-row disclosed after applying header %; fixed exchanges always 0. */
export function computeRowDisclosedQuantity(exchange, qty, headerPctNum) {
  if (exchangeForcesZeroDisclosed(exchange)) return 0;
  return disclosedQtyFromPercent(qty, headerPctNum);
}

/** Initial disclosed when opening modal from book row. */
export function initialModalDisclosedQuantity(isHolding, row) {
  if (exchangeForcesZeroDisclosed(row?.exchange)) return 0;
  return normalizePositiveQtyNumeric(isHolding ? row.t1_quantity || 0 : 0);
}

/** Value sent on place order (API). */
export function normalizeDisclosedQuantityForApi(exchange, rawDisclosed) {
  if (exchangeForcesZeroDisclosed(exchange)) return 0;
  return normalizePositiveQtyNumeric(rawDisclosed);
}

/** Apply header % to each row’s `disclosedQuantity` in modal overlay state. */
export function mergeDisclosedPercentIntoRows(prev, flatRows, pctNum) {
  const updated = { ...prev };
  for (const row of flatRows) {
    const modalId = row.modalId;
    const qtyRaw =
      updated[modalId]?.quantity !== undefined ? updated[modalId].quantity : row.quantity;
    const qty = normalizePositiveQtyNumeric(qtyRaw);
    updated[modalId] = {
      ...(updated[modalId] || {}),
      disclosedQuantity: computeRowDisclosedQuantity(row.exchange, qty, pctNum),
    };
  }
  return updated;
}
